/**
 * Canonical events dataset for Fenix Srl.
 *
 * Each item describes a single trade-fair / exhibition appearance. User-facing
 * copy (title, excerpt, body, location, venue) is referenced via translation
 * keys under `eventsData.items.{translationKey}.*` — see
 * `public/locales/{lang}/common.json`.
 *
 * Slugs are kebab-case and drive the URL segments under `/it/eventi/:slug`
 * and `/en/events/:slug` (IT and EN share the same slug by design).
 */

export interface FenixEvent {
  /** URL slug — kebab-case (e.g. `mecspe-2023`) */
  slug: string
  /**
   * Translation key suffix used under
   * `eventsData.items.{translationKey}.{title,excerpt,body,location,venue,alt}`.
   */
  translationKey: string
  /** ISO 8601 start date — `YYYY-MM-DD` */
  date: string
  /** Optional ISO 8601 end date for multi-day events */
  dateEnd?: string
  /** Primary image — absolute public path */
  image: string
  /** Optional external link (fair official site) */
  url?: string
  /**
   * ISO country code (2 letters) used only internally for sorting/filtering.
   * Display country is pulled from translations.
   */
  country: string
}

export const events: FenixEvent[] = [
  {
    slug: 'mecspe-2023',
    translationKey: 'mecspe2023',
    date: '2023-03-29',
    dateEnd: '2023-03-31',
    image: '/images/events/mecspe-2023.jpg',
    url: 'https://www.mecspe.com/',
    country: 'IT',
  },
  {
    slug: 'global-industrie-2023',
    translationKey: 'globalIndustrie2023',
    date: '2023-03-07',
    dateEnd: '2023-03-10',
    image: '/images/events/global-industrie-lyon.jpg',
    url: 'https://www.global-industrie.com/',
    country: 'FR',
  },
  {
    slug: 'mecspe-2021',
    translationKey: 'mecspe2021',
    date: '2021-11-23',
    dateEnd: '2021-11-25',
    image: '/images/events/mecspe-2021.jpg',
    url: 'https://www.mecspe.com/',
    country: 'IT',
  },
  {
    slug: 'grindtec-2020',
    translationKey: 'grindtec2020',
    date: '2020-03-18',
    dateEnd: '2020-03-21',
    image: '/images/events/grindtec-2020.jpg',
    url: 'https://www.grindtec.de/',
    country: 'DE',
  },
  {
    slug: 'mecspe-2020',
    translationKey: 'mecspe2020',
    date: '2020-03-26',
    dateEnd: '2020-03-28',
    image: '/images/events/mecspe-2020.png',
    url: 'https://www.mecspe.com/',
    country: 'IT',
  },
  {
    slug: 'emo-milano-2021',
    translationKey: 'emoMilano2021',
    date: '2021-10-04',
    dateEnd: '2021-10-09',
    image: '/images/events/global-industrie-stand.jpg',
    url: 'https://www.emo-milano.com/',
    country: 'IT',
  },
  {
    slug: 'mecspe-2024',
    translationKey: 'mecspe2024',
    date: '2024-03-06',
    dateEnd: '2024-03-08',
    image: '/images/events/mecspe-parma.jpg',
    url: 'https://www.mecspe.com/',
    country: 'IT',
  },
  {
    slug: 'global-industrie-2024',
    translationKey: 'globalIndustrie2024',
    date: '2024-03-25',
    dateEnd: '2024-03-28',
    image: '/images/events/global-industrie.jpg',
    url: 'https://www.global-industrie.com/',
    country: 'FR',
  },
]

/**
 * Returns the fully qualified i18n key for a given event field.
 * e.g. `eventKey(event, 'title')` → `eventsData.items.mecspe2023.title`
 */
export function eventKey(
  event: FenixEvent,
  field:
    | 'title'
    | 'excerpt'
    | 'body'
    | 'location'
    | 'venue'
    | 'city'
    | 'country'
    | 'alt'
): string {
  return `eventsData.items.${event.translationKey}.${field}`
}

/**
 * Lookup an event by its URL slug. Returns `undefined` if no match.
 */
export function getEventBySlug(slug: string): FenixEvent | undefined {
  return events.find((e) => e.slug === slug)
}

/**
 * Events sorted newest-first by ISO date (stable on ties).
 */
export function getEventsSortedByDate(): FenixEvent[] {
  return [...events].sort((a, b) =>
    a.date < b.date ? 1 : a.date > b.date ? -1 : 0
  )
}

/**
 * Returns true when the event's date (or its end date, if present) is in
 * the future relative to `now`. Used to split the listing into upcoming
 * versus past sections.
 */
export function isUpcoming(
  event: FenixEvent,
  now: Date = new Date()
): boolean {
  const end = event.dateEnd ?? event.date
  const endDate = new Date(end)
  if (isNaN(endDate.getTime())) return false
  // Include the full end day
  endDate.setHours(23, 59, 59, 999)
  return endDate.getTime() >= now.getTime()
}

/**
 * Returns upcoming events sorted by earliest start first.
 */
export function getUpcomingEvents(now: Date = new Date()): FenixEvent[] {
  return [...events]
    .filter((e) => isUpcoming(e, now))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

/**
 * Returns past events sorted newest-first.
 */
export function getPastEvents(now: Date = new Date()): FenixEvent[] {
  return [...events]
    .filter((e) => !isUpcoming(e, now))
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
}

/**
 * Returns up to `limit` related events (excluding the current slug).
 * Preference order: upcoming first, then most recent past.
 */
export function getRelatedEvents(
  current: FenixEvent,
  limit = 2,
  now: Date = new Date()
): FenixEvent[] {
  const upcoming = getUpcomingEvents(now).filter((e) => e.slug !== current.slug)
  const past = getPastEvents(now).filter((e) => e.slug !== current.slug)
  return [...upcoming, ...past].slice(0, limit)
}
